import { useMemo, useState } from 'react';
import { PlusIcon, RectangleStackIcon } from '@heroicons/react/24/outline';
import { createTable, TableColumnSpec, TableHeaderSpec } from '../../../../../../../components/table/CreateTable.tsx';
import { PrimaryButton } from '../../../../../../../components/general/PrimaryButton.tsx';
import { useNotifications } from '../../../../../../../lib/NotificationContext.tsx';
import { extractApiErrorMessage } from '../../../../../../../lib/apiErrors.ts';
import { SubledgerRowActionsMenu } from './SubledgerRowActionsMenu.tsx';
import SubledgerModal from './SubledgerModal.tsx';
import { Subledger, SubledgerFormValues } from './types.ts';

interface SubledgersSectionProps {
    subledgers: Subledger[];
    isLoading: boolean;
    onCreate: (name: string) => Promise<Subledger>;
    onRename: (subledgerId: string, name: string) => Promise<Subledger>;
}

export default function SubledgersSection({ subledgers, isLoading, onCreate, onRename }: SubledgersSectionProps) {
    const { dispatch } = useNotifications();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSubledger, setEditingSubledger] = useState<Subledger | null>(null);

    function openCreateModal() {
        setEditingSubledger(null);
        setIsModalOpen(true);
    }

    async function handleSubmit(values: SubledgerFormValues) {
        try {
            if (editingSubledger) {
                await onRename(editingSubledger.id, values.name);
            } else {
                await onCreate(values.name);
            }
            setIsModalOpen(false);
            setEditingSubledger(null);
            dispatch({
                title: editingSubledger ? 'Subledger bijgewerkt' : 'Subledger aangemaakt',
                description: editingSubledger
                    ? 'De naam is opgeslagen.'
                    : 'De subledger is toegevoegd.',
                type: 'success',
            });
        } catch (error) {
            dispatch({
                title: 'Opslaan mislukt',
                description:
                    extractApiErrorMessage(error) ??
                    'Er is een fout opgetreden. Probeer het later opnieuw.',
                type: 'error',
            });
            throw error;
        }
    }

    const takenNames = subledgers
        .filter((subledger) => subledger.id !== editingSubledger?.id)
        .map((subledger) => subledger.name.trim().toLowerCase());

    const SubledgersTable = useMemo(() => {
        const columns: TableColumnSpec<Subledger>[] = [
            {
                name: 'Naam',
                field: 'name',
                customRender: (row) => <span className="font-medium text-gray-900">{row.name}</span>,
            },
            {
                name: '',
                field: 'id',
                disableSorting: true,
                style: { widthMultiplier: 0.3, align: 'right', skeleton: 'none' },
                customRender: (row) => (
                    <div className="flex justify-end">
                        <SubledgerRowActionsMenu
                            onRename={() => {
                                setEditingSubledger(row);
                                setIsModalOpen(true);
                            }}
                        />
                    </div>
                ),
            },
        ];

        const tableSpec: TableHeaderSpec<Subledger> = {
            columns,
            isLoading,
            skeletonRows: 3,
            onEmptyRender: () => (
                <div className="text-center py-10 bg-gray-50 rounded-lg border border-gray-200">
                    <RectangleStackIcon className="mx-auto h-8 w-8 text-gray-300" />
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">Nog geen subledgers</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        Maak een subledger aan om boekingen van deze klant verder te onderverdelen.
                    </p>
                </div>
            ),
            options: {
                disableBottomPaginationWidget: true,
            },
        };

        return createTable<Subledger>([], tableSpec);
    }, [isLoading]);

    return (
        <div>
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">Subledgers</h3>
                    <p className="text-sm text-gray-500 mt-1">
                        Optionele labels om boekingen van deze klant verder te onderverdelen.
                    </p>
                </div>
                <PrimaryButton title="Nieuwe subledger" icon={PlusIcon} onClick={openCreateModal} />
            </div>

            <div className="mt-4">
                <SubledgersTable data={subledgers} />
            </div>

            <SubledgerModal
                open={isModalOpen}
                subledger={editingSubledger}
                takenNames={takenNames}
                onSubmit={handleSubmit}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingSubledger(null);
                }}
            />
        </div>
    );
}
